import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Session expired or not authenticated
      console.error('Session expired - please log in again');
      // Clear any stale user state and redirect to login
      if (window.location.pathname.startsWith('/admin') || 
          window.location.pathname.startsWith('/client') || 
          window.location.pathname.startsWith('/employee')) {
        // Store message for display after redirect
        sessionStorage.setItem('auth_error', 'Votre session a expiré. Veuillez vous reconnecter.');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Client APIs
export const clientApi = {
  getRecipes: (category) => api.get('/client/recipes', { params: { category } }),
  getMenus: () => api.get('/client/menus'),
  getPricing: () => api.get('/client/pricing'),
  checkSector: (postalCode) => api.get('/client/sectors/check', { params: { postal_code: postalCode } }),
  registerWaitingList: (data) => api.post('/client/sectors/waiting-list', data),
  getAvailableSlots: (postalCode, date) => api.get('/client/timeslots/available', { params: { postal_code: postalCode, date } }),
  getSmartSlots: (params) => api.get('/client/slots/smart', { params }),
  requestSlotAlert: (data) => api.post('/client/demand/alert-optin', data),
  createReservation: (data) => api.post('/client/reservations', data),
  getPromoEligibility: () => api.get('/client/promo/eligibility'),
  getMyReservations: () => api.get('/client/reservations'),
  getReservation: (id) => api.get(`/client/reservations/${id}`),
  getShoppingList: (id) => api.get(`/client/reservations/${id}/shopping-list`),
  downloadShoppingListPdf: (id) => `${API_URL}/api/client/reservations/${id}/shopping-list/pdf`,
  // Drive info (filled after booking)
  updateDriveInfo: (id, data) => api.put(`/client/reservations/${id}/drive`, data),
  clearDriveInfo: (id) => api.delete(`/client/reservations/${id}/drive`),
  // Cancellation
  getCancellationInfo: (id) => api.get(`/client/reservations/${id}/cancellation-info`),
  cancelReservation: (id, reason) => api.post(`/client/reservations/${id}/cancel`, { reason }),
  getBookingPolicy: () => api.get('/client/booking-policy'),
  // Payment Method
  getPaymentMethod: () => api.get('/client/payment-method'),
  setupPaymentMethod: () => api.post('/client/payment-method/setup'),
  confirmPaymentMethod: (data) => api.post('/client/payment-method/confirm', data),
  deletePaymentMethod: () => api.delete('/client/payment-method'),
  getPaymentHistory: () => api.get('/client/payment-history'),
  // Notifications
  getNotifications: () => api.get('/client/notifications'),
  markNotificationRead: (id) => api.put(`/client/notifications/${id}/read`),
  getUnreadNotificationsCount: () => api.get('/client/notifications/unread-count'),
  // Payment failures
  getPaymentFailures: () => api.get('/client/payment-failures'),
  regularizePayment: (failureId) => api.post(`/client/payment-failures/${failureId}/regularize`),
  // Recurring slots
  getRecurringEligibility: () => api.get('/client/recurring-slots/eligibility'),
  getRecurringSlots: () => api.get('/client/recurring-slots'),
  createRecurringSlot: (data) => api.post('/client/recurring-slots', data),
  updateRecurringSlot: (id, data) => api.put(`/client/recurring-slots/${id}`, data),
  deactivateRecurringSlot: (id) => api.post(`/client/recurring-slots/${id}/deactivate`),
  reactivateRecurringSlot: (id) => api.post(`/client/recurring-slots/${id}/reactivate`),
  // Kitchen Equipment (Client)
  getKitchenEquipmentList: () => api.get('/client/kitchen-equipment'),
  getKitchenProfile: () => api.get('/client/kitchen-profile'),
  saveKitchenProfile: (data) => api.post('/client/kitchen-profile', data),
  // Zone/Sector
  getZoneStatus: () => api.get('/client/zone-status'),
  checkSector: (postalCode) => api.get('/client/sectors/check', { params: { postal_code: postalCode } }),
  registerWaitingList: (data) => api.post('/client/sectors/waiting-list', data),
  // Referral
  getMyReferralCode: () => api.get('/client/referral/my-code'),
  validateReferralCode: (code) => api.get(`/client/referral/validate/${code}`),
  getMyReferralUses: () => api.get('/client/referral/my-uses'),
  // Seasons (for booking based on service date)
  getSeasonForDate: (serviceDate) => api.get('/client/seasons/for-date', { params: { service_date: serviceDate } }),  // NEW: Get recipes filtered by season for service date
  getRecipesForDate: (serviceDate) => api.get('/client/recipes/for-date', { params: { service_date: serviceDate } }),
  // NEW: Get menus filtered by season for service date
  getMenusForDate: (serviceDate) => api.get('/client/menus/for-date', { params: { service_date: serviceDate } }),
  // Avis clients (sous-lot B)
  getMyReview: () => api.get('/client/reviews/me'),
  submitReview: (data) => api.post('/client/reviews', data),
};

// Public APIs (no auth required)
export const publicApi = {
  getSettings: () => api.get('/settings/public'),
  getPageContent: (pageId) => api.get(`/pages/${pageId}`),
  getSAPPage: () => api.get('/sap-page'),
  getPublicConfig: () => api.get('/configuration/public'),
  // Avis clients (sous-lot B)
  getPublishedReviews: () => api.get('/reviews/published'),
  getFeaturedReview: () => api.get('/reviews/featured'),
  getCurrentMenu: () => api.get('/client/menus/current'),
  getPublicSectors: () => api.get('/sectors/public'),
  getPromoStatus: () => api.get('/promo/status'),
  submitContact: (data) => api.post('/contact', data),
  requestMagicLink: (email) => api.post('/auth/magic/request', { email }),
  verifyMagicLink: (token) => api.post('/auth/magic/verify', { token }),
};

// Admin APIs
export const adminApi = {
  // Archivage Google Drive
  getPromo: () => api.get('/admin/promo'),
  setPromoActive: (active) => api.put('/admin/promo', { active }),
  getDriveHealth: () => api.get('/admin/drive/health'),
  getDriveArchives: (params) => api.get('/admin/drive/archives', { params }),
  retryDriveArchives: () => api.post('/admin/drive/archive/retry'),
  retryDriveArchive: (queueId) => api.post(`/admin/drive/archive/retry/${queueId}`),
  archiveDriveTest: () => api.post('/admin/drive/archive/test'),
  archiveDriveTestCleanup: () => api.post('/admin/drive/archive/test/cleanup'),
  // Demande non satisfaite (statistiques de pilotage)
  getUnmetDemandStats: (params) => api.get('/admin/unmet-demand/stats', { params }),
  getUnmetDemandList: (params) => api.get('/admin/unmet-demand', { params }),
  resolveUnmetDemand: (id) => api.put(`/admin/unmet-demand/${id}/resolve`),
  // Configuration Center
  getConfiguration: () => api.get('/admin/configuration'),
  updateConfiguration: (data) => api.put('/admin/configuration', data),
  uploadMedia: (formData) => api.post('/admin/configuration/upload-media', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getConfigurationHistory: (limit = 20) => api.get('/admin/configuration/history', { params: { limit } }),
  restoreConfiguration: (version) => api.post(`/admin/configuration/restore/${version}`),
  resetConfiguration: () => api.post('/admin/configuration/reset'),
  addConfigLogo: (data) => api.post('/admin/configuration/logos', data),
  updateConfigLogo: (logoId, data) => api.put(`/admin/configuration/logos/${logoId}`, data),
  deleteConfigLogo: (logoId) => api.delete(`/admin/configuration/logos/${logoId}`),
  
  // Ingredients
  getIngredients: () => api.get('/admin/ingredients'),
  createIngredient: (data) => api.post('/admin/ingredients', data),
  updateIngredient: (id, data) => api.put(`/admin/ingredients/${id}`, data),
  deleteIngredient: (id) => api.delete(`/admin/ingredients/${id}`),
  
  // Recipes
  getRecipes: (params) => api.get('/admin/recipes', { params }),
  createRecipe: (data) => api.post('/admin/recipes', data),
  getRecipe: (id) => api.get(`/admin/recipes/${id}`),
  updateRecipe: (id, data) => api.put(`/admin/recipes/${id}`, data),
  deleteRecipe: (id) => api.delete(`/admin/recipes/${id}`),
  duplicateRecipe: (id) => api.post(`/admin/recipes/${id}/duplicate`),
  // Phase 1: ingrédients structurés + préparations maison + bascule moteur
  updateRecipeStructure: (id, data) => api.put(`/admin/recipes/${id}/structure`, data),
  getRecipesMigrationStatus: () => api.get('/admin/recipes-migration-status'),
  toggleIngredientEngine: (enable) => api.post('/admin/ingredient-engine/toggle', null, { params: { enable } }),
  listHomePreparations: () => api.get('/admin/home-preparations'),
  createHomePreparation: (data) => api.post('/admin/home-preparations', data),
  updateHomePreparation: (id, data) => api.put(`/admin/home-preparations/${id}`, data),
  deleteHomePreparation: (id) => api.delete(`/admin/home-preparations/${id}`),
  generateShoppingList: (recipeIds, servings) => api.post('/admin/recipes/shopping-list', recipeIds, { params: { servings } }),
  
  // Menus
  getMenus: (params) => api.get('/admin/menus', { params }),
  createMenu: (data) => api.post('/admin/menus', data),
  updateMenu: (id, data) => api.put(`/admin/menus/${id}`, data),
  duplicateMenu: (id) => api.post(`/admin/menus/${id}/duplicate`),
  deleteMenu: (id) => api.delete(`/admin/menus/${id}`),

  // Meal structure (permanent, unique)
  getMealStructure: () => api.get('/admin/meal-structure'),
  updateMealStructure: (categories) => api.put('/admin/meal-structure', categories),
  
  // Sectors
  getSectors: () => api.get('/admin/sectors'),
  getAddressExceptions: () => api.get('/admin/address-exceptions'),
  getQuantityAffected: () => api.get('/admin/ingredient-engine/affected'),
  getQuantityValidation: (id) => api.get(`/admin/ingredient-engine/validation/${id}`),
  getRecipeAudit: () => api.get('/admin/ingredient-engine/recipe-audit'),
  setQuantityEngineV2: (enable) => api.post('/admin/ingredient-engine/v2', { enable }),
  createAddressException: (data) => api.post('/admin/address-exceptions', data),
  updateAddressException: (id, data) => api.put(`/admin/address-exceptions/${id}`, data),
  deleteAddressException: (id) => api.delete(`/admin/address-exceptions/${id}`),
  getOutOfZoneAttempts: () => api.get('/admin/address-exceptions/attempts'),
  getOutOfZoneStats: () => api.get('/admin/address-exceptions/stats'),
  setOutOfZoneThreshold: (value) => api.put('/admin/out-of-zone/threshold', { open_threshold: value }),
  dismissOutOfZoneAttempt: (id) => api.post(`/admin/address-exceptions/attempts/${id}/dismiss`),
  createSector: (data) => api.post('/admin/sectors', data),
  updateSector: (id, data) => api.put(`/admin/sectors/${id}`, data),
  deleteSector: (id) => api.delete(`/admin/sectors/${id}`),
  
  // Employees
  getEmployees: () => api.get('/admin/employees'),
  createEmployee: (data) => api.post('/admin/employees', data),
  updateEmployee: (id, data) => api.put(`/admin/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/admin/employees/${id}`),
  
  // Employee Schedules (NEW)
  getEmployeeSchedule: (id) => api.get(`/admin/employees/${id}/schedule`),
  updateEmployeeSchedule: (id, data) => api.put(`/admin/employees/${id}/schedule`, data),
  getEmployeeExceptions: (id, params) => api.get(`/admin/employees/${id}/exceptions`, { params }),
  createEmployeeException: (id, data) => api.post(`/admin/employees/${id}/exceptions`, data),
  deleteEmployeeException: (id, date) => api.delete(`/admin/employees/${id}/exceptions/${date}`),
  bulkUpdateSchedule: (id, data) => api.post(`/admin/employees/${id}/schedule/bulk`, data),
  getAllSchedulesForWeek: (weekStart) => api.get('/admin/schedules/week', { params: { week_start: weekStart } }),
  
  // Time Slots
  getTimeSlots: (params) => api.get('/admin/timeslots', { params }),
  getEmployeePlanning: (employeeId, params) => api.get(`/admin/employees/${employeeId}/planning`, { params }),
  createTimeSlot: (data) => api.post('/admin/timeslots', data),
  generateTimeSlots: (employeeId, startDate, weeks) => api.post('/admin/timeslots/generate', null, { params: { employee_id: employeeId, start_date: startDate, weeks } }),
  addExceptionDay: (employeeId, date, action) => api.post('/admin/timeslots/exception', null, { params: { employee_id: employeeId, date, action } }),
  deleteTimeSlot: (id) => api.delete(`/admin/timeslots/${id}`),
  getDayWorkload: (employeeId, date) => api.get('/admin/workload/day', { params: { employee_id: employeeId, date } }),
  getWorkloadConfig: () => api.get('/admin/workload/config'),
  
  // Reservations
  getReservations: (params) => api.get('/admin/reservations', { params }),
  assignEmployee: (id, employeeId, slotId) => api.put(`/admin/reservations/${id}/assign`, null, { params: { employee_id: employeeId, slot_id: slotId } }),
  rescheduleReservation: (id, startTime) => api.put(`/admin/reservations/${id}/reschedule`, { start_time: startTime }),
  markAsTest: (id) => api.post(`/admin/reservations/${id}/test`),
  // Cancellations
  getCancellations: (lateOnly) => api.get('/admin/reservations/cancellations', { params: { late_only: lateOnly } }),
  adminCancelReservation: (id, reason) => api.post(`/admin/reservations/${id}/cancel`, { reason }),
  forceRefund: (id, reason) => api.post(`/admin/reservations/${id}/force-refund`, { reason }),
  
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUserRole: (id, role) => api.put(`/admin/users/${id}/role`, null, { params: { role } }),
  
  // Statistics
  getStatistics: () => api.get('/admin/statistics'),
  
  // Pricing
  getFormulaPrices: () => api.get('/admin/pricing/formulas'),
  createFormulaPrice: (data) => api.post('/admin/pricing/formulas', data),
  updateFormulaPrice: (id, data) => api.put(`/admin/pricing/formulas/${id}`, data),
  deleteFormulaPrice: (id, force = false) => api.delete(`/admin/pricing/formulas/${id}`, { params: { force } }),
  getOptionPrices: () => api.get('/admin/pricing/options'),
  createOptionPrice: (data) => api.post('/admin/pricing/options', data),
  updateOptionPrice: (id, data) => api.put(`/admin/pricing/options/${id}`, data),
  deleteOptionPrice: (id, force = false) => api.delete(`/admin/pricing/options/${id}`, { params: { force } }),
  
  // Site Settings
  getSiteSettings: () => api.get('/admin/settings'),
  updateSiteSettings: (data) => api.put('/admin/settings', data),
  
  // SAP Page Content
  getSAPPageContent: () => api.get('/admin/sap-page'),
  updateSAPPageContent: (data) => api.put('/admin/sap-page', data),
  resetSAPPageContent: () => api.post('/admin/sap-page/reset'),
  
  // Referrals
  getReferrals: () => api.get('/admin/referrals'),
  getReferralUses: () => api.get('/admin/referrals/uses'),
  getReferralStats: () => api.get('/admin/referrals/stats'),

  // Avis clients (sous-lot B)
  getReviews: (status) => api.get('/admin/reviews', { params: { status } }),
  moderateReview: (id, action) => api.put(`/admin/reviews/${id}/moderate`, null, { params: { action } }),
  featureReview: (id, featured = true) => api.put(`/admin/reviews/${id}/feature`, null, { params: { featured } }),
  
  // Out of Sector
  getOutOfSectorRequests: () => api.get('/admin/out-of-sector'),
  getOutOfSectorStats: () => api.get('/admin/out-of-sector/stats'),
  updateOutOfSectorStatus: (id, status, notes) => api.put(`/admin/out-of-sector/${id}/status`, null, { params: { status, notes } }),
  exportOutOfSectorCsv: () => `${API_URL}/api/admin/out-of-sector/export`,
  
  // Page Content (editable pages)
  getPages: () => api.get('/admin/pages'),
  getPage: (pageId) => api.get(`/admin/pages/${pageId}`),
  updatePage: (pageId, data) => api.put(`/admin/pages/${pageId}`, data),
  listPages: () => api.get('/admin/pages'),
  
  // Payment Failures
  getPaymentFailures: (status) => api.get('/admin/payment-failures', { params: { status } }),
  getPaymentFailuresStats: () => api.get('/admin/payment-failures/stats'),
  retryPayment: (failureId) => api.post(`/admin/payment-failures/${failureId}/retry`),
  forceReleaseSlot: (failureId) => api.post(`/admin/payment-failures/${failureId}/force-release`),
  markPaymentRegularized: (failureId) => api.post(`/admin/payment-failures/${failureId}/mark-regularized`),
  getCustomersWithCards: () => api.get('/admin/customers-with-cards'),
  
  // Financial Dashboard
  getFinancialOverview: (period) => api.get('/admin/finances/overview', { params: { period } }),
  getTransactions: (params) => api.get('/admin/finances/transactions', { params }),
  getRefunds: (params) => api.get('/admin/finances/refunds', { params }),
  getRecurringPayments: (params) => api.get('/admin/finances/recurring', { params }),
  processRefund: (transactionId, data) => api.post(`/admin/finances/refund/${transactionId}`, data),
  getAccountingPreview: (year, month) => api.get('/admin/finances/accounting-preview', { params: { year, month } }),
  getAccountingExportUrl: (year, month) => `${API_URL}/api/admin/finances/accounting-export?year=${year}&month=${month}`,
  
  // Recurring Slots
  getRecurringSlots: (activeOnly) => api.get('/admin/recurring-slots', { params: { active_only: activeOnly } }),
  getRecurringStats: () => api.get('/admin/recurring-slots/stats'),
  adminDeactivateRecurring: (id, reason) => api.post(`/admin/recurring-slots/${id}/deactivate`, { reason }),
  processRecurringSlots: () => api.post('/admin/recurring-slots/process'),
  
  // Seasons / Menu Automation
  getSeasons: (params) => api.get('/admin/seasons', { params }),
  getSeason: (id) => api.get(`/admin/seasons/${id}`),
  createSeason: (data) => api.post('/admin/seasons', data),
  updateSeason: (id, data) => api.put(`/admin/seasons/${id}`, data),
  deleteSeason: (id, archive = true) => api.delete(`/admin/seasons/${id}`, { params: { archive } }),
  duplicateSeason: (id) => api.post(`/admin/seasons/${id}/duplicate`),
  activateSeason: (id) => api.post(`/admin/seasons/${id}/activate`),
  addRecipesToSeason: (id, recipeIds) => api.post(`/admin/seasons/${id}/recipes`, { recipe_ids: recipeIds }),
  removeRecipeFromSeason: (seasonId, recipeId) => api.delete(`/admin/seasons/${seasonId}/recipes/${recipeId}`),
  getSeasonAlerts: () => api.get('/admin/seasons/alerts'),
  getSeasonsCalendar: (year) => api.get('/admin/seasons/calendar', { params: { year } }),
  checkSeasonForDate: (date) => api.get('/admin/seasons/check-date', { params: { date } }),
  getAvailableSeasons: () => api.get('/admin/seasons/available'),  // For recipe form dropdown
  updatePermanentSeason: (id, data) => api.put(`/admin/seasons/permanent/${id}`, data),
  
  // Emails
  getEmailLogs: (params) => api.get('/admin/emails', { params }),
  getEmailStats: () => api.get('/admin/emails/stats'),
  getEmailLog: (id) => api.get(`/admin/emails/${id}`),
  resendEmail: (id) => api.post(`/admin/emails/${id}/resend`),
  getEmailTypes: () => api.get('/admin/emails/types/list'),
  sendMenuReminders: () => api.post('/admin/emails/send-reminders'),
  sendTestEmail: (to) => api.post('/admin/emails/send-test', null, { params: { to } }),
  
  // Cron
  getCronLogs: (limit) => api.get('/cron/logs', { params: { limit } }),
  getCronConfig: () => api.get('/cron/config'),
  
  // Kitchen Equipment
  getKitchenEquipment: () => api.get('/admin/kitchen-equipment'),
  createKitchenEquipment: (data) => api.post('/admin/kitchen-equipment', data),
  updateKitchenEquipment: (id, data) => api.put(`/admin/kitchen-equipment/${id}`, data),
  deleteKitchenEquipment: (id) => api.delete(`/admin/kitchen-equipment/${id}`),
  getKitchenSettings: () => api.get('/admin/kitchen-settings'),
  updateKitchenSettings: (data) => api.put('/admin/kitchen-settings', data),
  getKitchenProfiles: (status) => api.get('/admin/kitchen-profiles', { params: { status } }),
  seedDefaultEquipment: () => api.post('/admin/kitchen-equipment/seed-defaults'),
  
  // Simulation Mode
  startSimulation: () => api.post('/admin/simulation/start'),
  stopSimulation: (cleanup = true) => api.post('/admin/simulation/stop', null, { params: { cleanup } }),
  getSimulationStatus: () => api.get('/admin/simulation/status'),
  createTestUser: () => api.post('/admin/simulation/create-test-user'),
  getVirtualSlots: (date) => api.get('/admin/simulation/virtual-slots', { params: { date } }),
  simulatePayment: (reservationId) => api.post('/admin/simulation/payment', null, { params: { reservation_id: reservationId } }),
  createSimulationReservation: (data) => api.post('/admin/simulation/reservation', data),
  getSimulationJournal: (simulationId) => api.get('/admin/simulation/journal', { params: { simulation_id: simulationId } }),
  getSimulationHistory: () => api.get('/admin/simulation/history'),
  getSimulationEmployeeView: (reservationId) => api.get(`/admin/simulation/employee-view/${reservationId}`),
  cleanupAllSimulationData: () => api.delete('/admin/simulation/cleanup-all'),
  
  // Audit Logs
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  getAuditLogsStats: () => api.get('/admin/audit-logs/stats'),
  exportAuditLogs: (params) => `${API_URL}/api/admin/audit-logs/export?${new URLSearchParams(params).toString()}`,
};

// Employee APIs
export const employeeApi = {
  getProfile: () => api.get('/employee/profile'),
  getInterventions: (status) => api.get('/employee/interventions', { params: { status } }),
  getPlanning: (date) => api.get('/employee/planning', { params: { date } }),
  validateIntervention: (id, checkoutComment) => api.put(`/employee/interventions/${id}/validate`, null, { params: { checkout_comment: checkoutComment } }),
  addNote: (id, note) => api.put(`/employee/interventions/${id}/note`, null, { params: { note } }),
  reportProblem: (id, problem) => api.post(`/employee/interventions/${id}/report`, null, { params: { problem } }),
  downloadFichePdf: (id) => `${API_URL}/api/employee/interventions/${id}/fiche/pdf`,
  getClientKitchenEquipment: (reservationId) => api.get(`/employee/interventions/${reservationId}/client-equipment`),
};

// Payment APIs
export const paymentApi = {
  createCheckout: (reservationId, originUrl) => api.post('/payments/checkout/create', { reservation_id: reservationId, origin_url: originUrl }),
  getCheckoutStatus: (sessionId) => api.get(`/payments/checkout/status/${sessionId}`),
};

export default api;
